import { concatBytes } from './escpos/commands';
import {
  encodeIsscXpP203aTestReceipt,
  encodeIsscXpP203aTestReceiptWithCut,
  encodePlainTextNotes,
  encodeXpP203aTestReceipt,
} from './escpos/encodeReceipt';
import { resolveXpP203aEscPosProfile } from './resolveXpP203aEscPosProfile';
import { shouldPrependXpP203aWake, type BleBridgeProfile } from './bleBridgeProfile';
import { formatBytesHex, formatPrinterLogText, getPrinterLogEntries } from './printerDebug';
import type { BluetoothPrinterService } from './BluetoothPrinterService';
import { getPayzheBtpProviderSnapshot } from './native-btp/PayzheBtpProvider';

export type PostPrintDebugReport = {
  path: string;
  ok: boolean;
  error?: string;
  at: number;
  logs: string;
  hex: string;
  combined: string;
};
import { getEffectivePrinterLinkSettings, type PrinterModelId } from './printerModelConfig';
import { XP_P203A_BLE_WAKE_BYTES } from './xpP203aBleConstants';

export async function buildTestPrintEscPosBytes(
  modelId: PrinterModelId,
  bridgeProfile?: BleBridgeProfile | null,
  isscCreditFlow?: 'notify' | 'delay-only',
): Promise<Uint8Array> {
  if (modelId === 'xp-p203a') {
    const profile = resolveXpP203aEscPosProfile({
      transportKind: 'ble',
      bleDiagnostics:
        bridgeProfile === 'issc'
          ? {
              serviceUuid: '',
              characteristicUuid: '',
              writeMode: 'writeWithoutResponse',
              chunkBytes: 20,
              interChunkDelayMs: 4,
              postWriteSettleMs: 500,
              bridgeProfile: 'issc',
              isscCreditFlow,
            }
          : null,
    });
    if (profile === 'issc-cut') return encodeIsscXpP203aTestReceiptWithCut();
    if (profile === 'issc') return encodeIsscXpP203aTestReceipt();
    return await encodeXpP203aTestReceipt();
  }
  return encodePlainTextNotes(
    ['----------------', 'PRINTER TEST', 'Bluetooth OK', 'ESC/POS OK', '----------------'],
    'default',
    modelId,
  );
}

/** Hex dump of the test slip ESC/POS job and expected BLE GATT payload (wake depends on bridge). */
export async function buildTestPrintHexReport(
  modelId: PrinterModelId,
  bridgeProfile?: BleBridgeProfile | null,
  isscCreditFlow?: 'notify' | 'delay-only',
): Promise<string> {
  const bytes = await buildTestPrintEscPosBytes(modelId, bridgeProfile ?? null, isscCreditFlow);
  const profile = bridgeProfile ?? 'generic';
  const wakeOnWire = modelId === 'xp-p203a' && shouldPrependXpP203aWake(profile);

  const lines: string[] = [
    `Printer model: ${modelId}`,
    `Bridge profile: ${profile}`,
    `Wake on BLE wire: ${wakeOnWire ? 'yes' : 'no'}`,
    `ESC/POS job: ${bytes.length} bytes`,
    '',
    '--- ESC/POS (what encodeReceipt produces) ---',
    formatBytesHex(bytes),
  ];

  if (modelId === 'xp-p203a') {
    if (wakeOnWire) {
      const wake = new Uint8Array(XP_P203A_BLE_WAKE_BYTES);
      const blePayload = concatBytes(wake, bytes);
      lines.push(
        '',
        `--- BLE GATT payload (${XP_P203A_BLE_WAKE_BYTES}-byte wake + ESC/POS) — NUS/vendor ---`,
        `${blePayload.length} bytes total`,
        formatBytesHex(blePayload),
      );
    } else {
      lines.push(
        '',
        '--- BLE GATT payload (ISSC: raw ESC/POS, no wake) ---',
        `${bytes.length} bytes total`,
        formatBytesHex(bytes),
      );
    }
  }

  return lines.join('\n');
}

/** Hex dump for the ESC/POS bytes from a completed print job. */
export function buildHexFromPrintBytes(
  bytes: Uint8Array,
  modelId: PrinterModelId,
  bridgeProfile?: BleBridgeProfile | null,
): string {
  const profile = bridgeProfile ?? 'generic';
  const wakeOnWire = modelId === 'xp-p203a' && shouldPrependXpP203aWake(profile);

  const lines: string[] = [
    `Printer model: ${modelId}`,
    `Bridge profile: ${profile}`,
    `Wake on BLE wire: ${wakeOnWire ? 'yes' : 'no'}`,
    `ESC/POS job: ${bytes.length} bytes`,
    '',
    '--- ESC/POS (sent to transport) ---',
    formatBytesHex(bytes),
  ];

  if (modelId === 'xp-p203a') {
    if (wakeOnWire) {
      const wake = new Uint8Array(XP_P203A_BLE_WAKE_BYTES);
      const blePayload = concatBytes(wake, bytes);
      lines.push(
        '',
        `--- BLE GATT payload (${XP_P203A_BLE_WAKE_BYTES}-byte wake + ESC/POS) — NUS/vendor ---`,
        `${blePayload.length} bytes total`,
        formatBytesHex(blePayload),
      );
    } else {
      lines.push(
        '',
        '--- BLE GATT payload (ISSC: raw ESC/POS, no wake) ---',
        `${bytes.length} bytes total`,
        formatBytesHex(bytes),
      );
    }
  }

  return lines.join('\n');
}

/** Full logs + HEX bundle after a print attempt (success or failure). */
export function buildPostPrintDebugReport(
  service: BluetoothPrinterService,
  opts: {
    path: string;
    ok: boolean;
    error?: string;
    escPosBytes?: Uint8Array;
    modelId: PrinterModelId;
    bridgeProfile?: BleBridgeProfile | null;
  },
): PostPrintDebugReport {
  const logs = buildPrinterLogsReport(service);
  const hex = opts.escPosBytes?.length
    ? buildHexFromPrintBytes(opts.escPosBytes, opts.modelId, opts.bridgeProfile)
    : '(no ESC/POS bytes captured for this job)';
  const header = [
    `=== Post-print debug (${opts.path}) ===`,
    `At: ${new Date().toISOString()}`,
    `Result: ${opts.ok ? 'OK' : 'FAILED'}`,
    opts.error ? `Error: ${opts.error}` : '',
  ]
    .filter(Boolean)
    .join('\n');
  const combined = `${header}\n\n--- LOGS ---\n${logs}\n\n--- HEX ---\n${hex}`;
  return {
    path: opts.path,
    ok: opts.ok,
    error: opts.error,
    at: Date.now(),
    logs,
    hex,
    combined,
  };
}

/** Connection, link settings, BLE diagnostics, native bridge last-write, and in-app log buffer. */
export function buildPrinterLogsReport(service: BluetoothPrinterService): string {
  const link = getEffectivePrinterLinkSettings();
  const ble = service.getBleDiagnostics();
  const sections: string[] = [
    '=== Zeettu printer debug ===',
    `Connection: ${service.getConnectionState()}`,
    `Active provider: ${service.getActiveProvider() ?? 'none'}`,
    `Transport: ${service.getActiveMode() ?? 'none'}`,
    `Model: ${link.modelId}`,
    `BLE chunk: ${link.bleChunkBytes} B, delay: ${link.bleInterChunkDelayMs} ms, settle: ${link.blePostWriteSettleMs} ms`,
    `BLE write with response: ${link.bleWriteWithResponse}`,
  ];

  if (service.getActiveProvider() === 'native') {
    const snap = getPayzheBtpProviderSnapshot();
    const lastWrite = snap.status?.lastWrite;
    sections.push(
      '',
      '--- payzhe BTP bridge ---',
      `Bridge state: ${snap.state}`,
      `Native transport: ${snap.status?.transport ?? 'unknown'}`,
      `Service running: ${snap.status?.serviceRunning ?? 'unknown'}`,
    );
    if (lastWrite) {
      sections.push(
        '--- Last native write ---',
        `Job: ${lastWrite.jobId ?? 'n/a'}`,
        `Bytes: ${lastWrite.jobBytes ?? 'n/a'}`,
        `Result: ${lastWrite.writeResult ?? 'n/a'}`,
        `Duration: ${lastWrite.writeDurationMs ?? 'n/a'} ms`,
        `Transport: ${lastWrite.transport ?? 'n/a'}`,
        `Bridge profile: ${lastWrite.bridgeProfile ?? 'n/a'}`,
        `Chunk: ${lastWrite.effectiveChunkSize ?? 'n/a'} B, delay: ${lastWrite.effectiveWriteDelayMs ?? 'n/a'} ms`,
        `Settle configured/applied: ${lastWrite.postWriteSettleMsConfigured ?? 'n/a'} / ${lastWrite.postWriteSettleMsApplied ?? 'n/a'} ms`,
        `Android API: ${lastWrite.androidApiLevel ?? 'n/a'}, vendor: ${lastWrite.vendorProfile ?? 'n/a'}, tier: ${lastWrite.compatibilityTier ?? 'n/a'}`,
        lastWrite.lastError ? `Error: ${lastWrite.lastError}` : '',
      );
    }
  }

  if (ble) {
    sections.push(
      '',
      '--- BLE GATT (last connect / write) ---',
      `Bridge profile: ${ble.bridgeProfile ?? 'unknown'}`,
      `Wake on write: ${ble.wakeOnWrite === undefined ? 'unknown' : ble.wakeOnWrite ? 'yes' : 'no'}`,
      `ISSC notify enabled: ${ble.isscNotifyEnabled === undefined ? 'n/a' : ble.isscNotifyEnabled ? 'yes' : 'no'}`,
      `ISSC credit flow: ${ble.isscCreditFlow ?? 'n/a'}`,
      `ISSC air patch: ${ble.isscAirPatchEnabled === undefined ? 'n/a' : ble.isscAirPatchEnabled ? 'yes' : 'no'}`,
      `ISSC ESC/POS: delay-only uses issc-cut (CRLF + GS V cut) + trailing CR on wire`,
      ble.explicitUuidFallback
        ? 'Note: saved/env BLE UUIDs failed — clear overrides on this page and reconnect.'
        : '',
      `Service: ${ble.serviceUuid}`,
      `Characteristic: ${ble.characteristicUuid}`,
      `Write mode: ${ble.writeMode}`,
      `WNR available: ${ble.writeWithoutResponseAvailable ?? 'unknown'}`,
      `Chunk bytes: ${ble.chunkBytes}`,
    );
  } else {
    sections.push('', '--- BLE GATT ---', '(not connected via Bluetooth)');
  }

  const last = service.getLastPrintSnapshot();
  if (last) {
    sections.push(
      '',
      '--- Last print job ---',
      `Path: ${last.path}`,
      `Bytes: ${last.byteLength}`,
      `Preview: ${last.preview}`,
    );
  }

  sections.push('', '--- In-app log buffer ---', formatPrinterLogText(getPrinterLogEntries()));

  return sections.join('\n');
}

/** Copy text to the system clipboard (HTTPS or localhost). */
export async function copyPrinterDebugText(text: string): Promise<boolean> {
  if (!text.trim()) return false;
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
